import { type FC, useState, useEffect } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/utils/core";

import type { PlaidAccount } from "@/shared/types/estateTypes";

interface AccountSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: PlaidAccount[];
  onSelect: (accountId: number) => void;
  isLoading?: boolean;
}

export const AccountSelectorDialog: FC<AccountSelectorDialogProps> = ({
  open,
  onOpenChange,
  accounts,
  onSelect,
  isLoading = false,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(
    accounts.length > 0 ? accounts[0].id : null,
  );

  useEffect(() => {
    setSelectedId(accounts.length > 0 ? accounts[0].id : null);
  }, [accounts]);

  const handleSubmit = () => {
    if (selectedId !== null) {
      onSelect(selectedId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Which is the estate account?</DialogTitle>
          <DialogDescription className="mt-1">Other accounts stay private</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {accounts.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-secondary" />
            </div>
          ) : (
          <div className="flex max-h-[360px] flex-col gap-2 overflow-y-auto">
            {accounts.map((account) => {
              const isSelected = account.id === selectedId;
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => setSelectedId(account.id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors",
                    isSelected
                      ? "border-brand bg-brand-subtle"
                      : "border-primary bg-white hover:bg-subtle",
                  )}
                >
                  <div
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
                      isSelected ? "border-brand bg-brand" : "border-primary bg-white",
                    )}
                    aria-hidden="true"
                  >
                    {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-primary">{account.name}</span>
                    <span className="text-sm text-secondary capitalize">{account.type}</span>
                  </div>
                </button>
              );
            })}
          </div>
          )}
        </DialogBody>
        <DialogFooter
          onSubmit={handleSubmit}
          submitText="Connect account"
          variant="brand"
          disabled={selectedId === null || isLoading}
          loading={isLoading}
          fullWidth
        />
      </DialogContent>
    </Dialog>
  );
};
